'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, CircularProgress, Container, Stack, TextField, Typography } from '@mui/material';
import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { AuthProviders } from '@/components/auth/auth-providers';
import { AppLink } from '@/components/mui/app-link';
import { authConfig } from '@/config/auth-config';
import { getHumanErrorMessage } from '@/libs/auth/auth-errors-utils';
import { getSafeCallbackUrl } from '@/libs/auth/redirect-utils';

import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email(),
});

export default function LoginPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { status } = useSession();
  const [isMounted, setIsMounted] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState<string | null>('');
  const [isEmailLoginSuccess, setIsEmailLoginSuccess] = React.useState<boolean | undefined>(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
    },
  });

  async function handleLoginSubmit(values: z.infer<typeof loginSchema>) {
    try {
      setIsLoading(true);
      setLoginError(null);
      setIsEmailLoginSuccess(false);

      const res = await signIn('nodemailer', {
        email: values.email,
        callbackUrl: getSafeCallbackUrl({
          requestedRedirect: searchParams?.get('redirect') ?? null,
          fallbackUrl: window.location.href,
          origin: typeof window !== 'undefined' ? window.location.origin : 'https://analisedefiis.com.br',
        }),
        redirect: false,
      });

      if (res?.error) {
        setLoginError('Não foi possível enviar o e-mail. Tente novamente.');
      } else {
        setIsEmailLoginSuccess(true);
      }
    } catch (error) {
      console.error('Error in login', error);
      setLoginError('Não foi possível enviar o e-mail. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }

  const errorMessage = loginError || searchParams?.get('error');
  const requestedRedirect = searchParams?.get('redirect') ?? null;

  React.useEffect(() => {
    if (status !== 'authenticated') return;

    router.replace(
      getSafeCallbackUrl({
        requestedRedirect,
        fallbackUrl: authConfig.normalUserCallbackUrl,
        origin: window.location.origin,
      }),
    );
  }, [requestedRedirect, router, status]);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth='sm'>
        {!isMounted ? (
          <Stack alignItems='center' py={6}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Typography variant='h5' component='h1' align='center' fontWeight={700}>
              Entrar ou criar uma conta
            </Typography>
            {isEmailLoginSuccess && (
              <Alert severity='success'>
                <strong>Sucesso!</strong> Por favor, verifique seu e-mail para o link mágico.
              </Alert>
            )}
            {errorMessage && (
              <Alert severity='error'>
                <strong>Erro!</strong> {getHumanErrorMessage(errorMessage)}
              </Alert>
            )}

            <Box component='form' onSubmit={handleSubmit(handleLoginSubmit)}>
              <Stack spacing={3}>
                <Controller
                  name='email'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label='E-mail'
                      type='email'
                      placeholder='john.doe@mail.com'
                      autoComplete='off'
                      inputProps={{
                        'data-lpignore': 'true',
                        'data-1p-ignore': 'true',
                        'data-form-type': 'other',
                        suppressHydrationWarning: true,
                      }}
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                      fullWidth
                    />
                  )}
                />
                <Button type='submit' variant='contained' disabled={isLoading} fullWidth>
                  {isLoading ? <CircularProgress size={20} /> : 'Entrar com E-mail'}
                </Button>
              </Stack>
            </Box>

            <Typography align='center' color='text.secondary'>
              Ou entre com
            </Typography>

            <AuthProviders requestedRedirect={requestedRedirect} />

            <Typography variant='body2' color='text.secondary' align='center'>
              Ao criar uma conta, você concorda com nossos{' '}
              <AppLink href='/termos' sx={{ fontWeight: 700, color: 'text.primary' }}>
                Termos de Serviço
              </AppLink>{' '}
              e{' '}
              <AppLink href='/privacidade' sx={{ fontWeight: 700, color: 'text.primary' }}>
                Política de Privacidade.
              </AppLink>
            </Typography>
          </Stack>
        )}
      </Container>
    </Box>
  );
}
